import { Edit } from '@mui/icons-material';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import {
  Avatar,
  Box,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import ModalsWrapper from 'src/components/Modals/ModalsWrapper';
import { HttpClient } from 'src/Helpers/http/http';
import { imgUrl, url } from 'src/Helpers/utils/Constant';
import Toaster from 'src/Toaster/Toaster';
import DeleteConfirmation from 'src/Utils/DeleteConfirmation';
import UpdateExecutive from './UpdateExecutive';
import { IExecutivePanel } from 'src/Types/module/ExecutivePanel/executivepanel';

interface IDeleteResponse {
  success: boolean;
  message: string;
  code?: number;
}

type ExecutivetableRowProps = {
  index: number;
  exeutiveMember: IExecutivePanel; // ideally IExecutivePanel
  exeutiveMembers: IExecutivePanel[];
  setExeutiveMembers: React.Dispatch<React.SetStateAction<any[] | null>>;
  setRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
};

export default function ExecutivetableRow({
  index,
  exeutiveMember,
  setExeutiveMembers,
  setRefreshTrigger,
  exeutiveMembers,
}: ExecutivetableRowProps) {
  const [open, setOpen] = useState<boolean>(false);
  const [editData, setEditData] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const theme = useTheme();
  const Toast = Toaster();

  const handleDelete = (id: number | string) => {
    (async function () {
      try {
        setIsLoading(true);
        const uri = `${url}/api/v1/admin/executive/${id}`;

        const data = await HttpClient.delete<IDeleteResponse>(uri);

        if (data.success) {
          const filterData = exeutiveMembers.filter((item) => item.id !== id);
          setExeutiveMembers(filterData);

          Toast.fire({
            icon: 'success',
            title: 'Deleted successfully!',
          });

          setIsLoading(false);
          setOpen(false);
          setRefreshTrigger((prev: number) => prev + 1);
        }
      } catch (error: any) {
        setIsLoading(false);
        Toast.fire({
          icon: 'error',
          title: `${error?.response?.data?.message || 'Delete failed'}`,
        });
      }
    })();
  };

  return (
    <>
      <TableRow hover>
        {/* SL */}
        <TableCell>
          <Typography variant='body1' color='text.primary' gutterBottom noWrap>
            {index + 1}
          </Typography>
        </TableCell>

        {/* Member Info: Name, Designation, Category, Duration */}
        <TableCell>
          <Typography variant='subtitle1' fontWeight={600} noWrap>
            {exeutiveMember?.name}
          </Typography>
          <Typography variant='body2' color='text.secondary' noWrap>
            Designation: {exeutiveMember?.position || 'N/A'}
          </Typography>
        </TableCell>
        {/* Agency & Contact: Agency, Mobile, Phone, Priority */}
        <TableCell>
          <Typography variant='body2' color='text.secondary' noWrap>
            Email: {exeutiveMember?.email || 'N/A'}
          </Typography>
          <Typography variant='body2' color='text.secondary' noWrap>
            Mobile: {exeutiveMember?.mobile || 'N/A'}
          </Typography>
          <Typography variant='body2' color='text.secondary' noWrap>
            Phone: {exeutiveMember?.phone || 'N/A'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant='body2' color='text.secondary' noWrap>
            {exeutiveMember?.category_name || 'N/A'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant='body2' color='text.secondary' noWrap>
            {exeutiveMember?.group_duration || 'N/A'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant='body2' color='text.secondary' noWrap>
            {exeutiveMember?.priority || 'N/A'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant='body2' color='text.primary' noWrap>
            {exeutiveMember?.agency_name || 'N/A'}
          </Typography>

          <Typography variant='body2' color='text.secondary' noWrap>
            Agency Designation: {exeutiveMember?.agency_designation || 'N/A'}
          </Typography>
        </TableCell>

        {/* Image */}
        <TableCell
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {exeutiveMember?.image ? (
            <Box
              component='img'
              src={`${imgUrl}/${exeutiveMember.image}`}
              alt={exeutiveMember.name}
              sx={{
                width: 60,
                height: 60,
                borderRadius: 1,
                boxShadow: 2,
                objectFit: 'cover',
              }}
            />
          ) : (
            <Avatar variant='rounded'>
              {exeutiveMember?.name?.[0] || 'E'}
            </Avatar>
          )}
        </TableCell>

        {/* Actions */}
        <TableCell>
          <Tooltip title='Edit' arrow>
            <IconButton
              sx={{
                '&:hover': {
                  background: theme.colors.primary.lighter,
                },
                color: theme.palette.primary.main,
              }}
              color='inherit'
              size='small'
              onClick={() => setEditData(true)}
            >
              <Edit fontSize='small' />
            </IconButton>
          </Tooltip>

          <Tooltip title='Delete' arrow>
            <IconButton
              onClick={() => setOpen(true)}
              sx={{
                '&:hover': { background: theme.colors.error.lighter },
                color: theme.palette.error.main,
              }}
              color='inherit'
              size='small'
            >
              <DeleteTwoToneIcon fontSize='small' />
            </IconButton>
          </Tooltip>

          <DeleteConfirmation
            open={open}
            handleClose={() => setOpen(false)}
            text={`Want to delete ${exeutiveMember.name} as an executive?`}
            handleDeleteFun={() => handleDelete(exeutiveMember?.id)}
            loading={isLoading}
          />
        </TableCell>
      </TableRow>

      {/* Edit Modal */}
      <ModalsWrapper
        setShowModal={setEditData}
        showModal={editData}
        modalData={{ title: 'Update Executive Member' }}
        maxWidth='sm'
      >
        <UpdateExecutive
          handleClose={setEditData}
          setRefreshTrigger={setRefreshTrigger}
          executive={exeutiveMember}
        />
      </ModalsWrapper>
    </>
  );
}
